import * as React from "react";
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import { IData, defaultContactpointItem } from "../../../store/contactpoint/ContactpointItem";
import { Card, CardActions, CardContent, CardHeader, Grid, Typography } from "@material-ui/core";

import { Organisatie } from "../../molecules/views/Organisatie";
import { Skeleton } from "@material-ui/lab";
import { Adres } from "../../molecules/views/Adres";
import { Stad } from "../../molecules/views/Stad";
import { Contactpersoon } from "../../molecules/views/Contactpersoon";
import { ContactInfo } from "../../molecules/views/ContactInfo";
import { AfspraakVereist } from "../../molecules/views/AfspraakVereist";
import { FaciliteitInfo } from "../../molecules/views/FaciliteitInfo";
import { Actief } from "../../molecules/views/Actief";
import { useTranslation } from 'react-i18next';
import { IDetailOrganism } from "../IDetailOrganism";
import { ContactpointIcon } from "../../atoms/icons/ContactpointIcon";
import { format } from 'date-fns';

// All the incoming values
interface IProps {

}

// Outgoing functions
interface IDispatch {

}

// Actual Component
export type AllProps = IProps & IDispatch & IDetailOrganism<IData>;
export const ContactpointDetail = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tContactpoint } = useTranslation('contactpoint');
    const { t: tCommon } = useTranslation('common');
    const data = props.data?.name ? props.data : defaultContactpointItem;
    
    // Utility function to check if a string has content (not null/undefined and not just whitespace)
    const hasContent = (value: string | null): boolean => {
        return value !== null && value !== undefined && value.trim() !== '';
    };
    
    // Helper function to safely get a string value (never null)
    const safeString = (value: string | null): string => {
        return value || '';
    };
    
    // Build full contact person name with salutation
    const contactPersonName = [
        data.salutation, 
        data.contactpersonFirstname, 
        data.contactpersonLastname
    ].filter(name => hasContent(name)).join(' ') || null;
    
    // Build full contact person info with function
    const contactPersonWithFunction = contactPersonName ? 
        [contactPersonName, data.contactpersonFunction].filter(info => hasContent(info)).join(' - ') :
        null;

    // Format dates if they exist
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '';
        try {
            return format(new Date(dateString), 'dd-MM-yyyy');
        } catch {
            return dateString;
        }
    };

    const formattedInactiveDate = formatDate(data.inactiveDate);
    const formattedContractDate = formatDate(data.contractDate);

    return (<Card>
        {props.loading ? 
            <CardHeader 
                title={<Skeleton variant="text" />} 
                subheader={<Skeleton variant="text" />} 
                avatar={<Skeleton variant="circle" />} 
            /> : 
            <CardHeader 
                title={tContactpoint('detailTitle', data)} 
                subheader={data.active ? tCommon('active') : tCommon('notActive')} 
                avatar={<ContactpointIcon />} 
            />
        }
        <CardContent>
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={2}>

                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tContactpoint('basisInformatieTitle')}
                    </Typography>

                    <Organisatie loading={props.loading} label={tContactpoint('naamLabel')} value={data.name} />
                    {hasContent(data.address) && (
                        <Adres loading={props.loading} value={safeString(data.address)} />
                    )}
                    {data.city && hasContent(data.city.cityName) && (
                        <Stad loading={props.loading} value={data.city} />
                    )}
                    <Actief loading={props.loading} value={data.active} />
                    {data.inactiveDate && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('inactiveDateLabel')} value={formattedInactiveDate} />
                    )}
                    {hasContent(data.contactpointPlus) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('contactpointPlusLabel')} value={safeString(data.contactpointPlus)} />
                    )}
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tContactpoint('contactpersoonTitle')}
                    </Typography>
                    
                    {contactPersonWithFunction && (
                        <Contactpersoon loading={props.loading} label={tContactpoint('contactpersoonLabel')} value={contactPersonWithFunction} />
                    )}
                    {hasContent(data.contactpersonEmail) && (
                        <ContactInfo loading={props.loading} type="email" label={tContactpoint('emailLabel')} value={safeString(data.contactpersonEmail)} />
                    )}
                    {hasContent(data.contactpersonEmail2) && (
                        <ContactInfo loading={props.loading} type="email" label={tContactpoint('email2Label')} value={safeString(data.contactpersonEmail2)} />
                    )}
                    {hasContent(data.contactpersonTelephone) && (
                        <ContactInfo loading={props.loading} type="phone" label={tContactpoint('telefoonLabel')} value={safeString(data.contactpersonTelephone)} />
                    )}
                    {hasContent(data.contactpersonMobile) && (
                        <ContactInfo loading={props.loading} type="mobile" label={tContactpoint('mobieltLabel')} value={safeString(data.contactpersonMobile)} />
                    )}
                    {hasContent(data.contactperson2) && (
                        <Contactpersoon loading={props.loading} label={tContactpoint('contactpersoon2Label')} value={safeString(data.contactperson2)} />
                    )}
                    {hasContent(data.contactDetails) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('contactDetailsLabel')} value={safeString(data.contactDetails)} />
                    )}
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tContactpoint('faciliteitenTitle')}
                    </Typography>

                    <AfspraakVereist loading={props.loading} label={tContactpoint('afspraakVereistLabel')} value={data.appointmentRequired} />
                    {hasContent(data.availability) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('beschikbaarheidLabel')} value={safeString(data.availability)} />
                    )}
                    {hasContent(data.appointmentInformation) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('afspraakInformatieLabel')} value={safeString(data.appointmentInformation)} />
                    )}
                    {hasContent(data.conversationSpace) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('gesprekruimteLabel')} value={safeString(data.conversationSpace)} />
                    )}
                    {hasContent(data.internet) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('internetLabel')} value={safeString(data.internet)} />
                    )}
                    {hasContent(data.drinking) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('drankLabel')} value={safeString(data.drinking)} />
                    )}
                    {hasContent(data.key) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('keyLabel')} value={safeString(data.key)} />
                    )}
                    {hasContent(data.alarm) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('alarmLabel')} value={safeString(data.alarm)} />
                    )}
                    {hasContent(data.heating) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('heatingLabel')} value={safeString(data.heating)} />
                    )}
                    {hasContent(data.flipover) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('flipoverLabel')} value={safeString(data.flipover)} />
                    )}
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Typography gutterBottom variant="h5" component="h2">
                        {tContactpoint('contractTitle')}
                    </Typography>

                    <AfspraakVereist loading={props.loading} label={tContactpoint('contractAvailableLabel')} value={data.contractAvailable} />
                    <AfspraakVereist loading={props.loading} label={tContactpoint('contractSavedLabel')} value={data.contractSaved} />
                    {data.contractDate && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('contractDateLabel')} value={formattedContractDate} />
                    )}
                    {hasContent(data.noContractReason) && (
                        <FaciliteitInfo loading={props.loading} label={tContactpoint('noContractReasonLabel')} value={safeString(data.noContractReason)} />
                    )}
                </Grid>

            </Grid>
        </CardContent>
        <CardActions>
            {props.actions}
        </CardActions>
    </Card>)
}

// CSS
const useStyles = makeStyles((theme: Theme) => ({ }));
